import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useStatementStore } from "../store/statementStore";
import { editedPdfUrl, exportPdfUrl, originalPdfUrl, waitForEditedPdf, downloadExportedPdf } from "../api/engine";
import ExportProgressBar from "../components/ExportProgressBar";
import PdfViewer from "../components/PdfViewer";

export default function ComparePage() {
    const { id } = useParams();
    const { statement, load, prepareExport, exporting, exportMessage, exportProgress } = useStatementStore();
    const [scaleA, setScaleA] = useState(1.4);
    const [ratio, setRatio] = useState(null);
    const [whichScrolling, setWhichScrolling] = useState(null);
    const [editedUrl, setEditedUrl] = useState(null);
    const [editedPreviewError, setEditedPreviewError] = useState(null);
    const [editedPreviewLoading, setEditedPreviewLoading] = useState(true);
    const leftRef = useRef(null);
    const rightRef = useRef(null);

    useEffect(() => {
        if (!statement || statement.id !== id) load(id);
    }, [id, statement, load]);

    useEffect(() => {
        let cancelled = false;
        async function prepareEditedPreview() {
            setEditedPreviewLoading(true);
            setEditedPreviewError(null);
            setEditedUrl(null);
            try {
                await waitForEditedPdf(id);
                if (!cancelled) {
                    setEditedUrl(`${editedPdfUrl(id)}#ready=${Date.now()}`);
                }
            } catch (err) {
                if (!cancelled) {
                    setEditedPreviewError(err.message || "Failed to prepare edited preview");
                }
            } finally {
                if (!cancelled) {
                    setEditedPreviewLoading(false);
                }
            }
        }
        prepareEditedPreview();
        return () => {
            cancelled = true;
        };
    }, [id, statement?.id]);

    function syncedScroll(source) {
        return (r) => {
            if (whichScrolling && whichScrolling !== source) return;
            setWhichScrolling(source);
            setRatio({ from: source, value: r });
            window.clearTimeout(syncedScroll._t);
            syncedScroll._t = window.setTimeout(() => setWhichScrolling(null), 80);
        };
    }

    const leftSync = ratio?.from === "right" ? ratio.value : undefined;
    const rightSync = ratio?.from === "left" ? ratio.value : undefined;

    async function handleExport(e, format = null) {
        if (e && e.preventDefault) e.preventDefault();
        try {
            await prepareExport();
            await downloadExportedPdf(id, statement?.filename, format);
        } catch {
            // Store owns the export error state.
        }
    }

    return (
        <div className="min-h-screen w-full flex flex-col">
            <header className="flex items-center justify-between px-6 py-3 border-b border-white/[0.06] bg-black/70 backdrop-blur-xl">
                <div className="flex items-center gap-4">
                    <Link
                        to={`/workspace/${id}`}
                        className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-400 hover:text-white"
                        data-testid="back-to-workspace"
                    >
                        ← Workspace
                    </Link>
                    <div className="w-px h-5 bg-white/10" />
                    <div className="flex flex-col">
                        <span className="text-[12px] font-mono text-zinc-300">
                            Side-by-side comparison
                        </span>
                        <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                            original ↔ edited · synchronized scroll
                        </span>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                        zoom
                        <input
                            type="range"
                            min="0.8"
                            max="2.2"
                            step="0.1"
                            value={scaleA}
                            onChange={(e) => setScaleA(parseFloat(e.target.value))}
                            data-testid="zoom-slider"
                            className="w-32 accent-blue-500"
                        />
                        <span className="text-zinc-300">
                            {scaleA.toFixed(1)}x
                        </span>
                    </div>
                    <a
                        href={exportPdfUrl(id)}
                        onClick={(e) => handleExport(e)}
                        className="btn-primary rounded-md px-3 py-1.5 text-[12px] font-medium min-w-[100px] text-center"
                        data-testid="compare-export-btn"
                    >
                        {exporting ? `${Math.round(exportProgress || 0)}%` : "Export PDF"}
                    </a>
                </div>
            </header>
            <ExportProgressBar
                active={exporting}
                message={exportMessage}
                progress={exportProgress}
            />

            <div className="grid grid-cols-2 flex-1 min-h-0">
                <div className="border-r border-white/[0.06] min-h-0">
                    <PdfViewer
                        ref={leftRef}
                        url={originalPdfUrl(id)}
                        scale={scaleA}
                        label="Original"
                        accent="#a1a1aa"
                        testId="pdf-original"
                        onScroll={syncedScroll("left")}
                        syncRatio={leftSync}
                    />
                </div>
                <div className="min-h-0">
                    {editedPreviewLoading && (
                        <div className="h-full grid place-items-center text-xs font-mono text-zinc-500">
                            Preparing edited preview…
                        </div>
                    )}
                    {editedPreviewError && !editedPreviewLoading && (
                        <div className="h-full grid place-items-center text-xs font-mono text-red-400 px-6 text-center">
                            {editedPreviewError}
                        </div>
                    )}
                    {editedUrl && !editedPreviewLoading && (
                        <PdfViewer
                            ref={rightRef}
                            url={editedUrl}
                            scale={scaleA}
                            label="Edited"
                            accent="#10b981"
                            testId="pdf-edited"
                            onScroll={syncedScroll("right")}
                            syncRatio={rightSync}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
